import { Box, IconButton, InputAdornment, Link as MuiLink, Stack, TextField, Typography } from '@mui/material'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import LoadingButton from '@mui/lab/LoadingButton'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import authApi from '../api/authApi'
import { fieldErrors, errorMessage } from '../utils/errors'
import { setToken } from '../utils/storage'
import { useToast } from '../components/common/ToastProvider'

const Signup = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState({})

  const handleSubmit = async (event) => {
    event.preventDefault()
    setErrors({})

    const data = new FormData(event.target)
    const username = String(data.get('username') || '').trim()
    const password = String(data.get('password') || '').trim()
    const confirmPassword = String(data.get('confirmPassword') || '').trim()

    const nextErrors = {}
    if (!username) nextErrors.username = 'Choose a username'
    else if (username.length < 8) nextErrors.username = 'Use at least 8 characters'
    if (!password) nextErrors.password = 'Choose a password'
    else if (password.length < 8) nextErrors.password = 'Use at least 8 characters'
    if (!confirmPassword) nextErrors.confirmPassword = 'Repeat your password'
    else if (password !== confirmPassword) nextErrors.confirmPassword = 'Passwords do not match'

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }

    setLoading(true)
    try {
      const res = await authApi.signup({ username, password, confirmPassword })
      setToken(res.token)
      navigate('/', { replace: true })
    } catch (err) {
      const fields = fieldErrors(err)
      setErrors(fields)
      if (Object.keys(fields).length === 0) {
        toast.error(errorMessage(err, 'Could not create your account.'))
      }
    } finally {
      setLoading(false)
    }
  }

  const passwordAdornment = (
    <InputAdornment position='end'>
      <IconButton
        edge='end'
        size='small'
        aria-label={showPassword ? 'Hide password' : 'Show password'}
        onClick={() => setShowPassword((visible) => !visible)}
      >
        {showPassword ? <VisibilityOffOutlinedIcon fontSize='small' /> : <VisibilityOutlinedIcon fontSize='small' />}
      </IconButton>
    </InputAdornment>
  )

  return (
    <Box>
      <Typography variant='h2' sx={{ mb: 1 }}>
        Create your account
      </Typography>
      <Typography color='text.secondary' sx={{ mb: 4 }}>
        Boards are private to you. Pick a username and you are in.
      </Typography>

      <Box component='form' onSubmit={handleSubmit} noValidate>
        <Stack spacing={2.5}>
          <TextField
            id='username'
            name='username'
            label='Username'
            autoComplete='username'
            autoFocus
            fullWidth
            disabled={loading}
            error={Boolean(errors.username)}
            helperText={errors.username || 'At least 8 characters'}
          />
          <TextField
            id='password'
            name='password'
            label='Password'
            type={showPassword ? 'text' : 'password'}
            autoComplete='new-password'
            fullWidth
            disabled={loading}
            error={Boolean(errors.password)}
            helperText={errors.password || 'At least 8 characters'}
            InputProps={{ endAdornment: passwordAdornment }}
          />
          <TextField
            id='confirmPassword'
            name='confirmPassword'
            label='Confirm password'
            type={showPassword ? 'text' : 'password'}
            autoComplete='new-password'
            fullWidth
            disabled={loading}
            error={Boolean(errors.confirmPassword)}
            helperText={errors.confirmPassword}
          />
          <LoadingButton
            type='submit'
            variant='contained'
            size='large'
            fullWidth
            loading={loading}
            sx={{ mt: 0.5 }}
          >
            Create account
          </LoadingButton>
        </Stack>
      </Box>

      <Typography variant='body2' color='text.secondary' sx={{ mt: 3 }}>
        Already have an account?{' '}
        <MuiLink component={Link} to='/login' underline='hover' color='primary.main' fontWeight={600}>
          Log in
        </MuiLink>
      </Typography>
    </Box>
  )
}

export default Signup
